'use client';

import { c as _c } from "react/compiler-runtime";
import { SendIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useCommentsContext } from '@/contexts/comments.context';
import { useCreatePostComment } from '@/hooks/queries/comment.query';
import { useIsHydrated } from '@/hooks/use-is-hydrated';
import { useSession } from '@/lib/auth-client';
import { Spinner } from '../ui/spinner';
import { CommentEditor } from './comment-editor';
import { UnauthenticatedOverlay } from './unauthenticated-overlay';
export function CommentPost() {
  const $ = _c(43);
  const {
    slug
  } = useCommentsContext();
  const [content, setContent] = useState("");
  const [tabsValue, setTabsValue] = useState("write");
  const isHydrated = useIsHydrated();
  const {
    data: session,
    isPending: isSessionLoading
  } = useSession();
  const t = useTranslations();
  let t0;
  if ($[0] !== slug) {
    t0 = {
      slug
    };
    $[0] = slug;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t) {
    t1 = () => {
      setContent("");
      toast.success(t("success.comment-posted"), {
        testId: "comment-posted-toast"
      });
      setTabsValue("write");
    };
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    mutate: createComment,
    isPending: isCreating
  } = useCreatePostComment(t0, t1);
  let t2;
  if ($[4] !== content || $[5] !== createComment || $[6] !== isCreating || $[7] !== slug || $[8] !== t) {
    t2 = function submitComment(e) {
      e?.preventDefault();
      if (isCreating) {
        return;
      }
      if (!content.trim()) {
        toast.error(t("error.comment-cannot-be-empty"));
        return;
      }
      createComment({
        slug,
        content: content.trim(),
        date: new Date().toISOString()
      });
    };
    $[4] = content;
    $[5] = createComment;
    $[6] = isCreating;
    $[7] = slug;
    $[8] = t;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const submitComment = t2;
  if (!isHydrated) {
    let t3;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="flex h-32.5 items-center justify-center"><Spinner /></div>;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    return t3;
  }
  const isAuthenticated = session !== null && !isSessionLoading;
  const disabled = !isAuthenticated || isCreating;
  let t3;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e_0 => {
      setContent(e_0.target.value);
    };
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = value => {
      setTabsValue(value as 'write' | 'preview');
    };
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t) {
    t5 = t("blog.comments.placeholder");
    $[13] = t;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== content || $[16] !== disabled || $[17] !== submitComment || $[18] !== t5 || $[19] !== tabsValue) {
    t6 = <CommentEditor value={content} onChange={t3} tabsValue={tabsValue} onTabsValueChange={t4} onModEnter={submitComment} placeholder={t5} disabled={disabled} data-testid="comment-textarea-post" />;
    $[15] = content;
    $[16] = disabled;
    $[17] = submitComment;
    $[18] = t5;
    $[19] = tabsValue;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== content || $[22] !== disabled) {
    t7 = disabled || !content.trim();
    $[21] = content;
    $[22] = disabled;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== t) {
    t8 = t("blog.comments.send-comment");
    $[24] = t;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== content || $[27] !== disabled) {
    t9 = disabled || !content.trim();
    $[26] = content;
    $[27] = disabled;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <SendIcon className="size-4" />;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t7 || $[31] !== t8 || $[32] !== t9) {
    t11 = <Button variant="ghost" size="icon-xs" className="absolute right-3 bottom-3" type="submit" disabled={t7} aria-label={t8} aria-disabled={t9} data-testid="comment-submit-button">{t10}</Button>;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== isAuthenticated) {
    t12 = isAuthenticated ? null : <UnauthenticatedOverlay />;
    $[34] = isAuthenticated;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t11 || $[37] !== t12 || $[38] !== t6) {
    t13 = <div className="relative">{t6}{t11}{t12}</div>;
    $[36] = t11;
    $[37] = t12;
    $[38] = t6;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== submitComment || $[41] !== t13) {
    t14 = <form onSubmit={submitComment}>{t13}</form>;
    $[40] = submitComment;
    $[41] = t13;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  return t14;
}